import { convertImageFromDto } from "@/modules/dtos/imageDto"
import type { SlotParticipantStatus } from "@/modules/profile/subscriber/gateway/subscriberDtos"
import { UserEntity } from "@/modules/profile/userProfile/userEntity"
import { ProgramType, type ProgramVisibility } from "@/modules/programs/types/programTypes"
import type {
  MobileSessionItemDto,
  MobileSlotItemDto,
  OnOffSessionItemDto,
} from "@/modules/training/gateway/trainingDto"
import type { HUImage } from "@/modules/types/imageTypes"

/* Entity from MobileSessionItemDto */
export class TrainingProgramInfiniteSessionEntity {
  id: string
  name: string
  description: string | null
  date: string | null
  owner: UserEntity | null
  programId: string
  programName: string
  programVisibility: ProgramVisibility
  programType: ProgramType.VIRTUAL | ProgramType.PRO
  hideSessionContent: boolean
  weightliftingTag: boolean
  gymnasticTag: boolean
  cardioTag: boolean
  restDay: boolean
  index: number | null
  virtualProgramByDateIndex: number | null

  private constructor(trainingProgramInfiniteSession: TrainingProgramInfiniteSessionEntity) {
    this.id = trainingProgramInfiniteSession.id
    this.name = trainingProgramInfiniteSession.name
    this.description = trainingProgramInfiniteSession.description
    this.date = trainingProgramInfiniteSession.date
    this.owner = trainingProgramInfiniteSession.owner
    this.programId = trainingProgramInfiniteSession.programId
    this.programName = trainingProgramInfiniteSession.programName
    this.programVisibility = trainingProgramInfiniteSession.programVisibility
    this.programType = trainingProgramInfiniteSession.programType
    this.hideSessionContent = trainingProgramInfiniteSession.hideSessionContent
    this.weightliftingTag = trainingProgramInfiniteSession.weightliftingTag
    this.gymnasticTag = trainingProgramInfiniteSession.gymnasticTag
    this.cardioTag = trainingProgramInfiniteSession.cardioTag
    this.restDay = trainingProgramInfiniteSession.restDay
    this.index = trainingProgramInfiniteSession.index
    this.virtualProgramByDateIndex = trainingProgramInfiniteSession.virtualProgramByDateIndex
  }

  static fromDto(dto: MobileSessionItemDto): TrainingProgramInfiniteSessionEntity {
    return new TrainingProgramInfiniteSessionEntity({
      id: dto.id,
      name: dto.name,
      description: dto.description,
      date: dto.date,
      owner: dto.owner ? UserEntity.fromDto(dto.owner) : null,
      programId: dto.programId,
      programName: dto.programName,
      programVisibility: dto.programVisibility,
      programType: dto.programType,
      hideSessionContent: dto.hideSessionContent ?? false,
      weightliftingTag: dto.weightliftingTag,
      gymnasticTag: dto.gymnasticTag,
      cardioTag: dto.cardioTag,
      restDay: dto.restDay,
      index: dto.index,
      virtualProgramByDateIndex: dto.virtualProgramByDateIndex,
    })
  }
}

/* Entity from OnOffSessionItemDto */
export class TrainingProgramOnOffSessionEntity {
  id: string
  name: string
  description: string | null
  date: string | null
  owner: UserEntity
  programId: string
  programName: string
  programVisibility: ProgramVisibility
  programType: ProgramType.ONOFF
  hideSessionContent: boolean | null
  weightliftingTag: boolean
  gymnasticTag: boolean
  cardioTag: boolean
  restDay: boolean
  index: number | null

  private constructor(trainingProgramOnOffSession: TrainingProgramOnOffSessionEntity) {
    this.id = trainingProgramOnOffSession.id
    this.name = trainingProgramOnOffSession.name
    this.description = trainingProgramOnOffSession.description
    this.date = trainingProgramOnOffSession.date
    this.owner = trainingProgramOnOffSession.owner
    this.programId = trainingProgramOnOffSession.programId
    this.programName = trainingProgramOnOffSession.programName
    this.programVisibility = trainingProgramOnOffSession.programVisibility
    this.programType = trainingProgramOnOffSession.programType
    this.hideSessionContent = trainingProgramOnOffSession.hideSessionContent
    this.weightliftingTag = trainingProgramOnOffSession.weightliftingTag
    this.gymnasticTag = trainingProgramOnOffSession.gymnasticTag
    this.cardioTag = trainingProgramOnOffSession.cardioTag
    this.restDay = trainingProgramOnOffSession.restDay
    this.index = trainingProgramOnOffSession.index
  }

  static fromDto(dto: OnOffSessionItemDto): TrainingProgramOnOffSessionEntity {
    return new TrainingProgramOnOffSessionEntity({
      id: dto.id,
      name: dto.name,
      description: dto.description,
      date: dto.date,
      owner: UserEntity.fromDto(dto.owner),
      programId: dto.programId,
      programName: dto.programName,
      programVisibility: dto.programVisibility,
      programType: ProgramType.ONOFF,
      hideSessionContent: dto.hideSessionContent,
      weightliftingTag: dto.weightliftingTag,
      gymnasticTag: dto.gymnasticTag,
      cardioTag: dto.cardioTag,
      restDay: dto.restDay,
      index: dto.index,
    })
  }
}

/* Entity from MobileSlotItemDto */
export class TrainingBoxSlotEntity {
  id: string
  startDate: string
  endDate: string
  program: {
    id: string
    name: string
    image: HUImage
    type: ProgramType
  }
  box: {
    id: string
    name: string
    event: boolean
    showSessionAttendees: boolean
  }
  hasSession: boolean
  hideSessionContent: boolean | null
  owner: UserEntity
  participantLimit: number | null
  registeredParticipantCount: number
  pendingParticipantCount: number
  participantStatus: SlotParticipantStatus
  location: string | null
  dropinsAccepted: boolean
  trialsAccepted: boolean
  waitingListPosition: number | null
  participantDropinsCount: number | null
  participantTrialCount: number | null
  guests: UserEntity[]
  assignedStaffMembers: UserEntity[]

  private constructor(trainingBoxSlot: TrainingBoxSlotEntity) {
    this.id = trainingBoxSlot.id
    this.startDate = trainingBoxSlot.startDate
    this.endDate = trainingBoxSlot.endDate
    this.program = trainingBoxSlot.program
    this.box = trainingBoxSlot.box
    this.hasSession = trainingBoxSlot.hasSession
    this.hideSessionContent = trainingBoxSlot.hideSessionContent
    this.owner = trainingBoxSlot.owner
    this.participantLimit = trainingBoxSlot.participantLimit
    this.registeredParticipantCount = trainingBoxSlot.registeredParticipantCount
    this.pendingParticipantCount = trainingBoxSlot.pendingParticipantCount
    this.participantStatus = trainingBoxSlot.participantStatus
    this.location = trainingBoxSlot.location
    this.dropinsAccepted = trainingBoxSlot.dropinsAccepted
    this.trialsAccepted = trainingBoxSlot.trialsAccepted
    this.waitingListPosition = trainingBoxSlot.waitingListPosition
    this.participantDropinsCount = trainingBoxSlot.participantDropinsCount
    this.participantTrialCount = trainingBoxSlot.participantTrialCount
    this.guests = trainingBoxSlot.guests
    this.assignedStaffMembers = trainingBoxSlot.assignedStaffMembers
  }

  static fromDto(dto: MobileSlotItemDto): TrainingBoxSlotEntity {
    return new TrainingBoxSlotEntity({
      id: dto.id,
      startDate: dto.startDate,
      endDate: dto.endDate,
      program: {
        id: dto.program.id,
        name: dto.program.name,
        image: convertImageFromDto(dto.program.image),
        type: dto.program.type,
      },
      box: dto.box,
      hasSession: dto.hasSession,
      hideSessionContent: dto.hideSessionContent,
      owner: UserEntity.fromDto(dto.owner),
      participantLimit: dto.participantLimit,
      registeredParticipantCount: dto.registeredParticipantCount,
      pendingParticipantCount: dto.pendingParticipantCount,
      participantStatus: dto.participantStatus,
      location: dto.location,
      dropinsAccepted: dto.dropinsAccepted,
      trialsAccepted: dto.trialsAccepted,
      waitingListPosition: dto.waitingListPosition,
      participantDropinsCount: dto.participantDropinsCount,
      participantTrialCount: dto.participantTrialCount,
      guests: dto.guests.map((guest) => UserEntity.fromDto(guest)),
      assignedStaffMembers: dto.assignedStaffMembers.map((staffMember) => UserEntity.fromDto(staffMember)),
    })
  }
}
